// BreadcrumbsComponent.js
import React from "react";
import { Breadcrumbs, Link, Typography, Box, Container } from "@mui/material";
import { useLocation, Link as RouterLink } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useRole } from "../../contexts/RoleContext";
import {
  containerStyles as breadcrumbsContainer,
  listStyles as breadcrumbsList,
  linkStyles as breadcrumbLink,
  homeIconStyles as breadcrumbHomeIcon,
  activeTextStyles as breadcrumbActiveText,
  separatorIconStyles as breadcrumbSeparatorIcon,
} from "../../assets/theme/breadcrumbsTheme";
import { CARE_PROFILE_ROUTE } from "../../constants/profileRoutes";

const BreadcrumbsComponent = () => {
  const location = useLocation();
  const { childrenWithAccess } = useRole();
  const isAuthenticatedRoute =
    location.pathname.startsWith("/dashboard") ||
    location.pathname.startsWith("/children") ||
    location.pathname.startsWith(CARE_PROFILE_ROUTE) ||
    location.pathname.startsWith("/care-knowledge") ||
    location.pathname.startsWith("/care-team") ||
    location.pathname.startsWith("/therapy-notes") ||
    location.pathname.startsWith("/templates") ||
    location.pathname.startsWith("/library") ||
    location.pathname.startsWith("/medical-log") ||
    location.pathname.startsWith("/providers") ||
    location.pathname.startsWith("/profile") ||
    location.pathname.startsWith("/messages") ||
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/invite") ||
    location.pathname.startsWith("/accept-invite");

  const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  // Get the current path and split it into an array
  const pathnames = location.pathname.split("/").filter((x) => x);

  // Map route segments to display names
  const breadcrumbNameMap = {
    dashboard: "Dashboard",
    messages: "Messages",
    dailyLog: "Daily Log",
    sensory: "Sensory",
    log: "Daily Log",
    templates: "Templates",
    library: "Library",
    add: "Add file",
    "natural-capture-debug": "Natural Capture Debug",
    "raw-capture-extractor": "Raw Capture Extractor",
    "care-team": "Care Circle",
    "care-knowledge": "Care Knowledge",
    "care-profile": "Care Profile",
    providers: "Providers",
    "medical-log": "Medical Log",
    "progress-notes": "Progress Notes",
    profile: "Profile",
    appointments: "Appointments",
  };

  // Build the breadcrumbs items
  const breadcrumbsItems = [];

  // Handle first crumb based on login status
  if (isAuthenticatedRoute) {
    // Always include the Dashboard link if not on the dashboard
    if (location.pathname !== "/dashboard") {
      breadcrumbsItems.push(
        <Link
          component={RouterLink}
          underline="hover"
          color="inherit"
          to="/dashboard"
          key="dashboard"
          sx={(theme) => breadcrumbLink(theme)}
        >
          <HomeIcon sx={(theme) => breadcrumbHomeIcon(theme)} />
          Dashboard
        </Link>
      );
    } else {
      breadcrumbsItems.push(
        <Typography
          color="text.primary"
          key="dashboard"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            fontWeight: 600,
          }}
        >
          <HomeIcon sx={(theme) => breadcrumbHomeIcon(theme)} />
          Dashboard
        </Typography>
      );
    }
  } else {
    // Not logged in
    // If pathname includes 'dashboard', skip rendering dashboard crumb
    if (!location.pathname.includes("dashboard")) {
      breadcrumbsItems.push(
        <Link
          component={RouterLink}
          underline="hover"
          color="inherit"
          to="/"
          key="home"
          sx={(theme) => breadcrumbLink(theme)}
        >
          <HomeIcon sx={(theme) => breadcrumbHomeIcon(theme)} />
          Home
        </Link>
      );
    }
  }

  // Build breadcrumbs for all routes
  let currentPath = "";
  pathnames.forEach((value, index) => {
    // Skip 'dashboard' as it's the root of our breadcrumbs
    if (value === "dashboard") {
      return;
    }

    // Build the path incrementally
    currentPath += `/${value}`;
    const isLast = index === pathnames.length - 1;
    const isChildIdSegment = pathnames[index - 1] === "children" && value !== "new";
    const childRecord = isChildIdSegment
      ? childrenWithAccess?.find((child) => child?.id === value)
      : null;
    const childName = String(childRecord?.name || "").trim();
    const rawName = isChildIdSegment
      ? (childName || "Child profile")
      : (breadcrumbNameMap[value] || value);
    const name = isChildIdSegment ? rawName : capitalize(rawName);
    const linkTarget = isChildIdSegment ? CARE_PROFILE_ROUTE : currentPath;
    const linkState = isChildIdSegment ? { childId: value } : undefined;
    const shouldRenderAsText = isChildIdSegment && !isLast;

    if (isLast || shouldRenderAsText) {
      breadcrumbsItems.push(
        <Typography color="text.primary" key={`${currentPath}-text`} sx={(theme) => breadcrumbActiveText(theme)}>
          {decodeURIComponent(name)}
        </Typography>
      );
    } else {
      breadcrumbsItems.push(
        <Link
          component={RouterLink}
          underline="hover"
          color="inherit"
          to={linkTarget}
          state={linkState}
          key={currentPath}
          sx={(theme) => breadcrumbLink(theme)}
        >
          {decodeURIComponent(name)}
        </Link>
      );
    }
  });

  return (
    <Box sx={(theme) => breadcrumbsContainer(theme)}>
      <Container
        disableGutters
        maxWidth="xl"
        sx={{ px: 2, background: "transparent" }}
      >
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" sx={(theme) => breadcrumbSeparatorIcon(theme)} />}
          aria-label="breadcrumb"
          sx={breadcrumbsList}
        >
          {breadcrumbsItems}
        </Breadcrumbs>
      </Container>
    </Box>
  );
};

export default BreadcrumbsComponent;
